import { inject, Injectable } from '@angular/core';
import {
  CompanyProfile,
  CompanyProfileDto,
  CompanyProfilePostBody,
  EnhancedCompanyProfileDto,
} from '@mkp/company/models';
import { LegalEntityDto, LegalEntityResource } from '@mkp/legal-entity/data-access';
import { ApiLink, CoreListEnvelop } from '@mkp/shared/models';
import {
  BaseHttpResource,
  extractGetOptions,
  GetWithQueryOptions,
  QueryParams,
} from '@mkp/shared/data-access';
import { map, Observable, of, switchMap, take } from 'rxjs';
import {
  mapCompanyProfileToCompanyRequestBody,
  mapEnhancedCompanyDtoToCompanyProfile,
} from './company.mapper';

export interface EnhancedCompanyEnvelop {
  companyProfiles: CompanyProfile[];
  totalCount: number;
  _links: { [key: string]: ApiLink };
  filter: string;
}

@Injectable({ providedIn: 'root' })
export class CompanyResource extends BaseHttpResource<
  CompanyProfileDto,
  CoreListEnvelop<CompanyProfileDto>,
  CompanyProfilePostBody
> {
  private legalEntityResource = inject(LegalEntityResource);

  constructor() {
    super('company-profile');
  }

  getCompany(companyId: string): Observable<CompanyProfile> {
    return this.getById(companyId).pipe(
      switchMap((company) => this.enhanceCompanyWithLegalEntity(company)),
      map(mapEnhancedCompanyDtoToCompanyProfile),
      take(1)
    );
  }

  getCompanies(
    paramsOrOptions: Partial<QueryParams> | GetWithQueryOptions
  ): Observable<EnhancedCompanyEnvelop> {
    const { params, headers, context } = extractGetOptions(paramsOrOptions);
    return this.getWithQuery(extractGetOptions({ params, headers, context })).pipe(
      switchMap((response) => {
        const companies = response._embedded.results;

        return this.enhanceCompaniesWithLegalEntities(companies).pipe(
          map((enhancedCompanies) => ({
            companyProfiles: enhancedCompanies.map(mapEnhancedCompanyDtoToCompanyProfile),
            totalCount: companies.length,
            filter: params.filter || '',
            _links: response._links ? { ...response._links } : {},
          }))
        );
      }),
      take(1)
    );
  }

  updateCompany(
    company: Partial<CompanyProfile> & Pick<CompanyProfile, 'id' | '_version'>
  ): Observable<CompanyProfile> {
    const companyDto = mapCompanyProfileToCompanyRequestBody(company);

    return this.update(company.id, companyDto).pipe(
      switchMap((updatedCompany) => this.enhanceCompanyWithLegalEntity(updatedCompany)),
      map(mapEnhancedCompanyDtoToCompanyProfile)
    );
  }

  private enhanceCompaniesWithLegalEntities(
    companies: CompanyProfileDto[]
  ): Observable<EnhancedCompanyProfileDto[]> {
    const legalEntityIds = [...new Set(companies.map((company) => company.legalEntityId))];

    if (legalEntityIds.length === 0) {
      return of(companies as EnhancedCompanyProfileDto[]);
    }

    return this.legalEntityResource.fetchLegalEntitiesByIds(legalEntityIds).pipe(
      map((legalEntities) => {
        const legalEntityMap = new Map(legalEntities.map((entity) => [entity.id, entity]));

        return companies.map((company) => {
          const legalEntity = legalEntityMap.get(company.legalEntityId);
          if (!legalEntity) return company as EnhancedCompanyProfileDto;

          return enhanceCompanyWithLegalEntityData(company, legalEntity);
        });
      })
    );
  }

  private enhanceCompanyWithLegalEntity(
    company: CompanyProfileDto
  ): Observable<EnhancedCompanyProfileDto> {
    return this.legalEntityResource
      .getById(company.legalEntityId)
      .pipe(map((legalEntity) => enhanceCompanyWithLegalEntityData(company, legalEntity)));
  }
}

const enhanceCompanyWithLegalEntityData = (
  company: CompanyProfileDto,
  legalEntity: LegalEntityDto
): EnhancedCompanyProfileDto => {
  return {
    ...company,
    legalEntityName: legalEntity.name,
    legalEntityAddress: legalEntity.address,
    legalEntityState: legalEntity.state,
    legalEntityVersion: legalEntity._version,
  };
};

export const getFilterForLegalEntityIds = (legalEntityIds: string[]) => ({
  filter: legalEntityIds.map((legalEntityId) => `legalEntity.id==${legalEntityId}`).join(','),
});
